import type { ReactNode } from 'react';
import { Group, Stack, Text } from '@wapharma/design-system';
import { FriendlyDate } from './FriendlyDate';

export type LifecycleTone = 'red' | 'orange' | 'green' | 'gray' | 'blue';

export type LifecycleStage = {
  id: string;
  title: string;
  /** Optional subtitle / descriptive sentence (body font). */
  subtitle?: ReactNode;
  /** ISO date string used to render a relative timestamp under the stage. */
  date?: string | null;
  /** Stage state for the dot/line palette. */
  state: 'done' | 'active' | 'pending' | 'cancelled';
  /** Pastel tone aligned with status visual signals. */
  tone: LifecycleTone;
  /** Optional badge override; defaults derive from state. */
  tagLabel?: string;
  /** Actor that performed this stage (rendered as "Effectué par X"). */
  actor?: { email?: string | null; source: 'USER' | 'SYSTEM' | 'STOREFRONT' | 'POS' };
  /** Optional payload-derived extra meta (e.g. PO number). */
  extraMeta?: ReactNode;
};

export type LifecycleTimelineProps = {
  stages: LifecycleStage[];
  /** Hide the per-stage state badge (Fait / Actuel / À venir / Annulé). */
  hideTags?: boolean;
  /** Nested in light panel (e.g. wide tooltip): stronger ink, no extra wash on pending rows. */
  panelInk?: boolean;
};

import { LIFECYCLE_INDICATOR_COLORS } from '../lib/statusPillColors';

export const TONE_PALETTE: Record<LifecycleTone, { line: string; indicator: string; fill: string }> = {
  green: {
    line: 'rgba(34, 197, 94, 0.22)',
    indicator: LIFECYCLE_INDICATOR_COLORS.success,
    fill: 'rgba(187, 247, 208, 0.34)',
  },
  orange: {
    line: 'rgba(249, 115, 22, 0.22)',
    indicator: LIFECYCLE_INDICATOR_COLORS.warning,
    fill: 'rgba(254, 215, 170, 0.36)',
  },
  red: {
    line: 'rgba(239, 68, 68, 0.22)',
    indicator: LIFECYCLE_INDICATOR_COLORS.danger,
    fill: 'rgba(254, 202, 202, 0.36)',
  },
  blue: {
    line: 'rgba(59, 130, 246, 0.24)',
    indicator: LIFECYCLE_INDICATOR_COLORS.info,
    fill: 'rgba(191, 219, 254, 0.35)',
  },
  gray: {
    line: 'rgba(148, 163, 184, 0.22)',
    indicator: LIFECYCLE_INDICATOR_COLORS.muted,
    fill: 'rgba(255, 255, 255, 0.9)',
  },
};

/** Fill + stroke for small SVG ring segments (matches timeline palette). */
export function lifecycleToneRingPaint(tone: LifecycleTone): { fill: string; stroke: string } {
  const p = TONE_PALETTE[tone];
  return {
    fill: p.fill,
    stroke: `color-mix(in srgb, ${p.indicator} 42%, white)`,
  };
}

function tagFor(stage: LifecycleStage): { label: string; bg: string; color: string; border: string } | null {
  const palette = TONE_PALETTE[stage.tone];
  if (stage.tagLabel === '__none__') return null;
  if (stage.tagLabel) {
    return {
      label: stage.tagLabel,
      bg: `color-mix(in srgb, ${palette.fill} 82%, white)`,
      color: `color-mix(in srgb, ${palette.indicator} 78%, var(--wp-text-primary))`,
      border: `1px solid color-mix(in srgb, ${palette.indicator} 34%, white)`,
    };
  }
  switch (stage.state) {
    case 'cancelled':
      return {
        label: 'Annulé',
        bg: `color-mix(in srgb, ${palette.fill} 82%, white)`,
        color: `color-mix(in srgb, ${palette.indicator} 78%, var(--wp-text-primary))`,
        border: `1px solid color-mix(in srgb, ${palette.indicator} 34%, white)`,
      };
    case 'done':
      return {
        label: 'Fait',
        bg: `color-mix(in srgb, ${palette.fill} 82%, white)`,
        color: `color-mix(in srgb, ${palette.indicator} 78%, var(--wp-text-primary))`,
        border: `1px solid color-mix(in srgb, ${palette.indicator} 34%, white)`,
      };
    case 'active':
      return {
        label: 'Actuel',
        bg: `color-mix(in srgb, ${palette.fill} 82%, white)`,
        color: `color-mix(in srgb, ${palette.indicator} 78%, var(--wp-text-primary))`,
        border: `1px solid color-mix(in srgb, ${palette.indicator} 34%, white)`,
      };
    case 'pending':
    default:
      return {
        label: 'À venir',
        bg: `color-mix(in srgb, ${palette.fill} 82%, white)`,
        color: `color-mix(in srgb, ${palette.indicator} 72%, var(--wp-text-primary))`,
        border: `1px solid color-mix(in srgb, ${palette.indicator} 34%, white)`,
      };
  }
}

/**
 * Render the actor line for a stage. Falls back to a friendly source label when
 * no email is available (storefront customer, POS terminal, system job).
 */
function renderActor(stage: LifecycleStage): ReactNode {
  const a = stage.actor;
  if (!a) return null;
  if (a.email) {
    return (
      <Text size="xs" c="dimmed" style={{ fontFamily: 'var(--wp-font-body)' }}>
        Effectué par <strong style={{ color: 'var(--wp-text-primary)' }}>{a.email}</strong>
      </Text>
    );
  }
  const sourceLabel =
    a.source === 'STOREFRONT'
      ? 'Boutique en ligne'
      : a.source === 'POS'
        ? 'Caisse / POS'
        : a.source === 'SYSTEM'
          ? 'Système'
          : null;
  if (!sourceLabel) return null;
  return (
    <Text size="xs" c="dimmed" style={{ fontFamily: 'var(--wp-font-body)' }}>
      Effectué via <strong style={{ color: 'var(--wp-text-primary)' }}>{sourceLabel}</strong>
    </Text>
  );
}

export function LifecycleTimeline({ stages, hideTags, panelInk }: LifecycleTimelineProps) {
  return (
    <section aria-label="Cycle de vie" style={{ display: 'grid', gap: 'var(--wp-space-8)', padding: '2px 0' }}>
      <Stack gap="sm">
        {stages.map((stage, i) => {
          const isLast = i === stages.length - 1;
          const palette = TONE_PALETTE[stage.tone];
          const tag = hideTags ? null : tagFor(stage);
          const isActive = stage.state === 'active';
          const isPending = stage.state === 'pending';

          return (
            <div
              key={stage.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '30px minmax(0, max-content)',
                columnGap: 14,
                width: 'max-content',
                maxWidth: '100%',
              }}
            >
              <div
                aria-hidden
                style={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  paddingTop: 2,
                }}
              >
                {!isLast ? (
                  <div
                    style={{
                      position: 'absolute',
                      top: 24,
                      bottom: -12,
                      width: 2,
                      background: palette.line,
                    }}
                  />
                ) : null}
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 999,
                    border: `1.5px solid color-mix(in srgb, ${palette.indicator} 72%, white)`,
                    background: palette.fill,
                    boxShadow: isActive ? '0 0 0 6px rgba(15,23,42,0.03)' : 'none',
                  }}
                />
              </div>

              <div
                style={{
                  minWidth: 0,
                  width: 'auto',
                  boxSizing: 'border-box',
                  justifySelf: 'start',
                  maxWidth: 'min(100%, 34rem)',
                  padding: '8px 0',
                  paddingBottom: isLast ? 0 : 8,
                }}
              >
                <Group justify="space-between" wrap="wrap" gap={8}>
                  <Group gap={8} wrap="wrap" align="center">
                    <Text
                      fw={isActive ? 700 : isPending ? 500 : 600}
                      size={isActive ? 'md' : 'sm'}
                      c={
                        panelInk
                          ? isPending
                            ? 'primary'
                            : undefined
                          : isPending
                            ? 'dimmed'
                            : undefined
                      }
                      style={{
                        fontFamily: 'var(--wp-font-display)',
                        lineHeight: 1.15,
                        opacity: panelInk ? 1 : isPending ? 0.78 : 1,
                      }}
                    >
                      {stage.title}
                    </Text>
                    {tag ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '2px 8px',
                          borderRadius: 999,
                          background: tag.bg,
                          color: tag.color,
                          border: tag.border,
                          fontSize: 11,
                          fontWeight: 800,
                          letterSpacing: '0.02em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {tag.label}
                      </span>
                    ) : null}
                  </Group>
                </Group>

                {stage.subtitle ? (
                  <Text
                    size="sm"
                    c={panelInk ? undefined : isPending || !isActive ? 'dimmed' : undefined}
                    style={{
                      marginTop: 4,
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                      fontFamily: 'var(--wp-font-body)',
                      opacity: panelInk ? 1 : isPending ? 0.82 : 1,
                      ...(panelInk && (isPending || !isActive) ? { color: 'var(--wp-feldspar-600)' } : {}),
                    }}
                  >
                    {stage.subtitle}
                  </Text>
                ) : null}

                {stage.date ? (
                  <Text size="xs" c="dimmed" style={{ marginTop: 6, fontFamily: 'var(--wp-font-body)' }}>
                    <FriendlyDate iso={stage.date} />
                  </Text>
                ) : null}

                {stage.extraMeta ? (
                  <Text size="xs" c="dimmed" style={{ marginTop: 4, fontFamily: 'var(--wp-font-body)' }}>
                    {stage.extraMeta}
                  </Text>
                ) : null}

                {stage.actor ? (
                  <div style={{ marginTop: 4 }}>{renderActor(stage)}</div>
                ) : null}
              </div>
            </div>
          );
        })}
      </Stack>
    </section>
  );
}

/** Map an arbitrary status string to a pastel tone consistent with status dots. */
export function lifecycleToneForOrder(status: string): LifecycleTone {
  const s = String(status ?? '').toUpperCase();
  if (s === 'DELIVERED') return 'green';
  if (s === 'CANCELLED' || s === 'RETURNED') return 'red';
  return 'orange';
}

export function lifecycleToneForSaleQuote(status: string): LifecycleTone {
  const s = String(status ?? '').toUpperCase();
  if (s === 'CONVERTED' || s === 'ACCEPTED') return 'green';
  if (s === 'REJECTED' || s === 'EXPIRED' || s === 'CANCELLED') return 'red';
  return 'orange';
}

export function lifecycleToneForProcurementQuote(status: string): LifecycleTone {
  const s = String(status ?? '').toUpperCase();
  if (s === 'AWARDED') return 'green';
  if (s === 'CANCELLED' || s === 'EXPIRED') return 'red';
  return 'orange';
}

export function lifecycleToneForPo(status: string): LifecycleTone {
  const s = String(status ?? '').toUpperCase();
  if (s === 'COMPLETED') return 'green';
  if (s === 'CANCELLED') return 'red';
  return 'orange';
}

export function lifecycleToneForSupportTicket(status: string): LifecycleTone {
  const s = String(status ?? '').toUpperCase();
  if (s === 'CLOSED') return 'gray';
  if (s === 'RESOLVED') return 'green';
  if (s === 'IN_PROGRESS') return 'blue';
  return 'orange';
}

export function lifecycleToneForBatch(status: string): LifecycleTone {
  const s = String(status ?? '').toUpperCase();
  if (s === 'ACTIVE') return 'green';
  if (s === 'NEAR_EXPIRY') return 'orange';
  if (s === 'EXPIRED') return 'red';
  if (s === 'RECALLED') return 'gray';
  return 'orange';
}
